import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import CreateServiceDrawer from './CreateServiceDrawer';
import EditServiceDrawer from './EditServiceDrawer';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  suspendido: 'bg-red-100 text-red-800',
};

const TYPE_COLORS = {
  recurring: 'bg-blue-100 text-blue-800',
  one_time: 'bg-yellow-100 text-yellow-800',
};

const ServicesTable = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const fetchServices = async () => {
    try {
      const res = await api.get('/api/services');
      setServices(res.data);
    } catch (err) {
      console.error('Error al cargar servicios:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleEditClick = (service) => {
    setSelectedService(service);
    setShowEditDrawer(true);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-4 sm:px-6 px-4 lg:px-8 font-[Manrope]">
      <div className="mx-auto max-w-7xl">
        <div className="bg-white shadow-md rounded-xl p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl sm:text-xl font-semibold text-gray-900">Servicios</h1>
              <p className="mt-1 text-sm text-gray-600">
                Lista de todos los servicios creados para los planes activos.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setShowCreateDrawer(true)}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              >
                + Servicio
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : services.length === 0 ? (
            <div className="text-center text-gray-500 py-12">No hay servicios registrados.</div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600">
                    <tr>
                      <th className="px-4 py-3">Nombre</th>
                      <th className="px-4 py-3">Tipo</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3">Categoría</th>
                      <th className="px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {services.map((service) => (
                      <tr key={service.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 font-medium text-gray-900">{service.name}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                              TYPE_COLORS[service.billing_type] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {service.billing_type === 'one_time' ? 'Único' : 'Recurrente'}
                          </span>
                          </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                              STATUS_COLORS[service.status] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {service.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-700">{service.category || 'N/A'}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleEditClick(service)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4">
                {services.map((service) => (
                  <div
                    key={service.id}
                    className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">{service.name}</span>
                        <button
                          onClick={() => handleEditClick(service)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Editar
                        </button>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Tipo: </span>
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                            TYPE_COLORS[service.billing_type] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {service.billing_type === 'one_time' ? 'Único' : 'Recurrente'}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Estado: </span>
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                            STATUS_COLORS[service.status] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {service.status}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Categoría: </span>
                        <span className="text-sm text-gray-700">{service.category || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Drawers */}
      <CreateServiceDrawer
        isOpen={showCreateDrawer}
        onClose={() => setShowCreateDrawer(false)}
        onCreated={fetchServices}
      />
      <EditServiceDrawer
        isOpen={showEditDrawer}
        service={selectedService}
        onClose={() => {
          setShowEditDrawer(false);
          setSelectedService(null);
        }}
        onUpdated={fetchServices}
      />
    </div>
  );
};

export default ServicesTable;